/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
// import { useTranslation } from 'react-i18next';
import { createWaterDropStyle } from '../styles/glass-effects';
import { useGlassStyles } from '@hooks/useGlassStyles';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (type: ToastType, message: string, duration?: number) => void;
  hideToast: (id: string) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // const { t } = useTranslation();

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (type: ToastType, message: string, duration: number = 3000) => {
      const id = Math.random().toString(36).substr(2, 9);
      const newToast: Toast = { id, type, message, duration };

      setToasts((prev) => [...prev, newToast]);

      // Auto hide after duration
      setTimeout(() => {
        hideToast(id);
      }, duration);
    },
    [hideToast],
  );

  const showSuccess = useCallback(
    (message: string, duration?: number) => {
      showToast('success', message, duration);
    },
    [showToast],
  );

  const showError = useCallback(
    (message: string, duration?: number) => {
      showToast('error', message, duration);
    },
    [showToast],
  );

  const showInfo = useCallback(
    (message: string, duration?: number) => {
      showToast('info', message, duration);
    },
    [showToast],
  );

  const showWarning = useCallback(
    (message: string, duration?: number) => {
      showToast('warning', message, duration);
    },
    [showToast],
  );

  const value: ToastContextType = {
    toasts,
    showToast,
    hideToast,
    showSuccess,
    showError,
    showInfo,
    showWarning,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onHide={hideToast} />
    </ToastContext.Provider>
  );
};

// Toast Container Component
interface ToastContainerProps {
  toasts: Toast[];
  onHide: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onHide }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-[10000] space-y-2 px-4 w-auto max-w-md">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onHide={onHide} />
      ))}
    </div>
  );
};

// Individual Toast Item Component
interface ToastItemProps {
  toast: Toast;
  onHide: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onHide }) => {
  const getToastStyles = (type: ToastType) => {
    const waterDropStyle = createWaterDropStyle();

    switch (type) {
      case 'success':
        return {
          className: 'bg-gradient-to-r from-green-500/80 to-emerald-500/80 text-black',
          style: {
            ...waterDropStyle,
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.4), rgba(16, 185, 129, 0.4))',
            borderColor: 'rgba(34, 197, 94, 0.5)',
          },
        };
      case 'error':
        return {
          className: 'bg-gradient-to-r from-red-500/80 to-rose-500/80 text-black',
          style: {
            ...waterDropStyle,
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.4), rgba(244, 63, 94, 0.4))',
            borderColor: 'rgba(239, 68, 68, 0.5)',
          },
        };
      case 'warning':
        return {
          className: 'bg-gradient-to-r from-yellow-500/80 to-amber-500/80 text-black',
          style: {
            ...waterDropStyle,
            background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.4), rgba(245, 158, 11, 0.4))',
            borderColor: 'rgba(234, 179, 8, 0.5)',
          },
        };
      case 'info':
        return {
          className: 'bg-gradient-to-r from-blue-500/80 to-cyan-500/80 text-black',
          style: {
            ...waterDropStyle,
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(6, 182, 212, 0.4))',
            borderColor: 'rgba(59, 130, 246, 0.5)',
          },
        };
      default:
        return {
          className: 'bg-gradient-to-r from-gray-500/80 to-slate-500/80 text-black',
          style: {
            ...waterDropStyle,
            background:
              'linear-gradient(135deg, rgba(107, 114, 128, 0.4), rgba(100, 116, 139, 0.4))',
            borderColor: 'rgba(107, 114, 128, 0.5)',
          },
        };
    }
  };

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        );
      case 'info':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const toastStyle = getToastStyles(toast.type);

  const { glassStyle } = useGlassStyles(toast.type);
  const waterDropStyle = createWaterDropStyle();

  return (
    <div
      className={`
        flex items-center rounded-full
        transform transition-all duration-300 ease-in-out
        animate-in slide-in-from-bottom-2
        relative overflow-hidden
        animate-toast-expand
        will-change-transform transform-gpu
        min-w-[40px] max-w-full w-full sm:max-w-[480px] sm:w-auto
        ${toastStyle.className}
      `}
      style={
        {
          ...toastStyle.style,
          position: 'relative' as const,
          ...(glassStyle as React.CSSProperties),
          ...(waterDropStyle as React.CSSProperties),
          backfaceVisibility: 'hidden',
          perspective: '1000px',
          transformStyle: 'preserve-3d',
        } as React.CSSProperties
      }
    >
      {/* Icon with circular background - always visible */}
      <div className="flex-shrink-0 relative">
        <div className="w-10 h-10 flex items-center justify-center">
          <div className="animate-fade-in-scale">{getIcon(toast.type)}</div>
        </div>
      </div>

      {/* Text content with expand animation */}
      <div
        className="flex-1 animate-text-expand-delay overflow-hidden will-change-transform transform-gpu"
        style={{
          backfaceVisibility: 'hidden',
          perspective: '1000px',
        }}
      >
        <div className="px-3 py-1 transform transition-transform duration-300 transform-gpu">
          <p className="text-sm font-medium whitespace-nowrap sm:whitespace-nowrap overflow-hidden text-ellipsis transform transition-opacity duration-300 transform-gpu">
            {toast.message}
          </p>
        </div>
      </div>

      {/* Close button with delayed animation */}
      <div
        className="flex-shrink-0 animate-close-button-expand-delay will-change-transform transform-gpu"
        style={{
          backfaceVisibility: 'hidden',
          perspective: '1000px',
        }}
      >
        <button
          onClick={() => onHide(toast.id)}
          className="p-2 mr-2 hover:bg-white/20 rounded-full transition-all duration-300 backdrop-blur-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
